import React, { useState } from "react";
import { Dialog, Flex, Text, TextField, Button, Callout } from "@radix-ui/themes";
import { InfoCircledIcon, PlusIcon } from "@radix-ui/react-icons";
import { ArrowTopRightOnSquareIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import { useSendGoogleAdsInvitation } from "hooks";
import { useToast } from "../../../context/ToastContext";
import { useProfileContext } from "context";

export function AuthGoogleAdsDialog({
  isOpen,
  onOpenChange,
  onContinue,
  googleAdsConfigs = [],
  setGoogleAdsConfigs,
  isLoading,
  invalidIds = [],
  title = "Connect Google Ads Account",
}) {
  const [newAccountId, setNewAccountId] = useState("");
  const [inputError, setInputError] = useState(""); // Shared error state for input and continue validation
  const [sentInvitations, setSentInvitations] = useState({});
  const [loadingInvitations, setLoadingInvitations] = useState({});

  const { userProfile } = useProfileContext();
  const sendGoogleAdsInvitation = useSendGoogleAdsInvitation();
  const { setToastMessage } = useToast();

  // Add new Google Ads account to list
  const handleAddAccount = () => {
    if (!newAccountId?.trim()) {
      setInputError("Input Google Ads Account ID.");
      return;
    }

    const formatted = newAccountId.replace(/[^0-9]/g, ""); // normalize digits only

    if (formatted.length !== 10) {
      setInputError("Please enter a valid 10-digit Google Ads Account ID.");
      return;
    }

    // Clear error if valid
    setInputError("");

    const newConfig = {
      id: Date.now(),
      googleAdsAccountId: formatted,
    };
    setGoogleAdsConfigs((prev) => [...prev, newConfig]);
    setNewAccountId("");
  };

  const handleRemoveAccount = (id) => {
    setGoogleAdsConfigs((prev) => prev.filter((acc) => acc.id !== id));
  };

  const handleSendInvitation = async (accountId) => {
    try {
      // Set loading for this button
      setLoadingInvitations((prev) => ({ ...prev, [accountId]: true }));

      const result = await sendGoogleAdsInvitation.mutateAsync({
        account_id: accountId,
        organisation_id: userProfile?.organisation_id,
      });

      if (result?.data?.status === "success") {
        setSentInvitations((prev) => ({ ...prev, [accountId]: true }));
      } else {
        setToastMessage(result?.data?.message || "Failed to send invitation.");
      }
    } catch (err) {
      setToastMessage(
        err?.response?.data?.message ||
          "An unexpected error occurred while sending the invitation."
      );
    } finally {
      // Clear loading state
      setLoadingInvitations((prev) => ({ ...prev, [accountId]: false }));
    }
  };

  const handleContinue = () => {
    if (googleAdsConfigs.length === 0) {
      setInputError("Input at least one Google Ads account to integrate.");
      return;
    }
    setInputError("");
    onContinue();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="600px" align="center">
        <Dialog.Title>{title}</Dialog.Title>

        <Dialog.Description size="2" mb="4">
          Link one or more Google Ads accounts to your organisation. After connecting, you can manage them by 
          brand or location. To continue, send an invitation for each Google Ads account you want to connect.
        </Dialog.Description>

        <Callout.Root size="1" mb="4" color="amber">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            You need to accept the invitation(s) to continue and integrate successfully.
          </Callout.Text>
        </Callout.Root>

        {/* Add Account Input */}
        <Flex direction="column" gap="1" mb="3">
          {/* Input + Button on the same row */}
          <Flex direction="row" gap="3" align="center">
            <TextField.Root
              placeholder="Enter Google Ads Account ID (e.g. 123-456-7890)"
              value={newAccountId}
              onChange={(e) => setNewAccountId(e.target.value)}
              size="2"
              variant="surface"
              style={{ flex: 1 }}
            />
            <Button size="2" variant="soft" onClick={handleAddAccount}>
              <Flex align="center" gap="1">
                <PlusIcon width={16} height={16} /> Add
              </Flex>
            </Button>
          </Flex>

          {/* Error message below the input + button row */}
          {inputError && (
            <Text size="1" color="red" style={{ marginTop: "2px" }}>
              {inputError}
            </Text>
          )}
        </Flex>

        {/* Connected Accounts List */}
          <Flex
            direction="column"
            gap="2"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {googleAdsConfigs.map((acc) => {
              const isInvalid = invalidIds.includes(acc.googleAdsAccountId);

              return (
                <Flex
                  key={acc.id}
                  direction="row"
                  justify="between"
                  align="center"
                  style={{
                    padding: "6px 8px",
                    border: `1px solid ${isInvalid ? "red" : "var(--gray-a6)"}`,
                    borderRadius: "var(--radius-2)",
                    backgroundColor: isInvalid ? "var(--red-3)" : "transparent",
                  }}
                >
                  <Text size="2" style={{ fontFamily: "monospace" }}>
                    {acc.googleAdsAccountId.length === 10
                      ? `${acc.googleAdsAccountId.slice(0, 3)}-${acc.googleAdsAccountId.slice(3, 6)}-${acc.googleAdsAccountId.slice(6)}`
                      : acc.googleAdsAccountId}
                  </Text>

                  <Flex gap="2" align="center">
                    {isInvalid && (
                      <Text size="1" color="red">
                        Invalid ID
                      </Text>
                    )}

                    {sentInvitations[acc.googleAdsAccountId] ? (
                      <Button
                        size="1"
                        variant="soft"
                        color="grass"
                        style={{ pointerEvents: "none", opacity: 1 }}
                      >
                        <Flex align="center" gap="1">
                          <CheckCircleIcon width={14} height={14} /> Sent
                        </Flex>
                      </Button>
                    ) : (
                      <Button
                        size="1"
                        variant="soft"
                        loading={loadingInvitations[acc.googleAdsAccountId] || false}
                        onClick={() =>
                          handleSendInvitation(acc.googleAdsAccountId)
                        }
                      >
                        Send invitation
                      </Button>
                    )}

                    <Button size="1" variant="soft" color="red" onClick={() => handleRemoveAccount(acc.id)}>
                      Remove
                    </Button>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>

        {/* Footer */}
        <Flex direction="column" gap="2" mt="4">
          <Flex justify="between" align="center">
            <Button
              size="2"
              variant="ghost"
              color="gray"
              onClick={() =>
                window.open(
                  "https://ads.google.com/home/tools/manager-accounts/",
                  "_blank"
                )
              }
            >
              <Flex align="center" gap="2">
                <Text weight="medium">Open Google Ads manager</Text>
                <ArrowTopRightOnSquareIcon width={16} height={16} />
              </Flex>
            </Button>
            
            {/* Action buttons */}
            <Flex gap="3">
              <Dialog.Close>
                <Button size="2" variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={handleContinue}
                size="2"
                variant="solid"
                loading={isLoading}
                disabled={invalidIds.length > 0} // prevent continue if invalid IDs exist
              >
                Continue
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
